import { useMemo, useState } from "react";
import { Card, DataList, FieldSelect, Modal, Stack, Table, type TableColumn } from "@knowtex-ai/components";
import { Download, Eye } from "iconoir-react";
import type { McEvent } from "../../lib/mc";
import ClampedText from "./ClampedText";
import { GAP, SectionHeading } from "./layout";
import RowActions from "./RowActions";
import { saveEventJson } from "./download";
import { formatCount } from "./format";
import { ALL_EVENTS, eventFilters } from "./runData";
import TimeCell from "./TimeCell";
import { useRetained } from "./useRetained";

const MAX_PAYLOAD_CHARS = 200_000;

const TYPE_CLASSES: Record<string, string> = {
  "tool.error": "text-red-700 bg-red-50",
  finding: "text-amber-800 bg-amber-50",
  uncertainty: "text-amber-800 bg-amber-50",
  observation: "text-blue-800 bg-blue-50",
  "voice.utterance": "text-teal-800 bg-teal-50",
};

const typeClass = (type: string) =>
  TYPE_CLASSES[type] ||
  (type.startsWith("mission.") || type.startsWith("run.") ? "text-purple-800 bg-purple-50" : "text-gray-700 bg-gray-100");

const TypeTag = ({ type }: { type: string }) => (
  <span className={`rounded px-1.5 py-0.5 font-mono text-[12px] ${typeClass(type)}`}>{type}</span>
);

const payloadText = (payload: unknown) => {
  const text = JSON.stringify(payload, null, 2) ?? "";
  return text.length > MAX_PAYLOAD_CHARS ? `${text.slice(0, MAX_PAYLOAD_CHARS)}\n… truncated; download the JSON for all of it` : text;
};

function EventModal({ runId, event: selected, onClose }: { runId: string; event: McEvent | null; onClose: () => void }) {
  const event = useRetained(selected);
  return (
    <Modal
      open={!!selected}
      onOpenChange={(open) => !open && onClose()}
      title={event ? `Event #${formatCount(event.seq)}` : "Event"}
      size="large"
    >
      {event && (
        <Stack gap={GAP.section}>
          <DataList
            size="small"
            aria-label={`Event ${event.seq}`}
            items={[
              { id: "type", label: "Type", value: <TypeTag type={event.type} /> },
              { id: "mission", label: "Mission", value: event.missionId || "—" },
              { id: "at", label: "Time", value: <TimeCell iso={event.at} /> },
              {
                id: "summary",
                label: "Summary",
                value: <p className="whitespace-pre-wrap break-words">{event.summary || "—"}</p>,
              },
            ]}
          />
          <div>
            <div className="mb-3 flex items-center justify-between">
              <SectionHeading className="!mb-0">Payload</SectionHeading>
              <RowActions
                label="Event actions"
                actions={[{ key: "json", label: "Download JSON", icon: Download, onClick: () => saveEventJson(runId, event) }]}
              />
            </div>
            {event.payload === undefined || event.payload === null ? (
              <p className="text-sm text-gray-500">No payload.</p>
            ) : (
              <pre className="max-h-[480px] overflow-auto whitespace-pre-wrap break-words rounded-md bg-gray-50 p-3 font-mono text-[12px] text-gray-700">
                {payloadText(event.payload)}
              </pre>
            )}
          </div>
        </Stack>
      )}
    </Modal>
  );
}

interface RunEventsProps {
  runId: string;
  events: McEvent[];
  loading: boolean;
  live: boolean;
}

export default function RunEvents({ runId, events, loading, live }: RunEventsProps) {
  const [filterId, setFilterId] = useState(ALL_EVENTS.id);
  const [open, setOpen] = useState<McEvent | null>(null);
  const filters = useMemo(() => eventFilters(events), [events]);
  const filter = filters.find((f) => f.id === filterId) ?? filters[0];
  const visible = useMemo(() => events.filter((event) => filter.matches(event.type)), [events, filter]);

  const columns = useMemo(
    (): TableColumn<McEvent>[] => [
      { id: "seq", header: "#", width: 70, align: "end", accessor: "seq", sortable: true, cell: (e) => formatCount(e.seq) },
      { id: "at", header: "Time", width: 160, accessor: "at", cell: (e) => <TimeCell iso={e.at} timeOnly className="font-mono" /> },
      { id: "mission", header: "Mission", width: 90, accessor: "missionId" },
      { id: "type", header: "Type", width: 160, accessor: "type", cell: (e) => <TypeTag type={e.type} /> },
      {
        id: "summary",
        header: "Summary",
        width: 560,
        grow: true,
        accessor: "summary",
        cell: (e) => <ClampedText text={e.summary} lines={2} />,
      },
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        width: 64,
        sticky: "end",
        hideable: false,
        cell: (e) => (
          <RowActions
            label={`Actions for event ${e.seq}`}
            actions={[
              { key: "view", label: "View details", icon: Eye, onClick: () => setOpen(e) },
              { key: "json", label: "Download JSON", icon: Download, onClick: () => saveEventJson(runId, e) },
            ]}
          />
        ),
      },
    ],
    [runId]
  );

  return (
    <Card
      variant="elevated"
      title="Activity"
      subtitle={`${formatCount(events.length)} events${live ? ", updating live" : ""}`}
      actions={
        <FieldSelect
          aria-label="Show events"
          size="small"
          items={filters.map((f) => ({ value: f.id, label: `${f.label} (${formatCount(f.count)})` }))}
          value={filter.id}
          onChange={(value) => setFilterId(value || ALL_EVENTS.id)}
        />
      }
    >
      <Table
        aria-label="Run events"
        size="small"
        columns={columns}
        data={visible}
        getRowId={(e) => String(e.seq)}
        loading={loading}
        defaultPageSize={100}
        pageSizeOptions={[50, 100, 250, 500]}
        maxHeight={640}
        emptyState="No events yet."
      />
      <EventModal runId={runId} event={open} onClose={() => setOpen(null)} />
    </Card>
  );
}
